import { useMemo, useState } from "react";
import { AlertCircle, CalendarClock, CalendarDays, CheckCircle2, ChevronDown, ChevronRight, LoaderCircle, Map, RefreshCw, Sparkles } from "lucide-react";
import { isEventActionable, sortCatalogEvents } from "../catalog";
import type { Catalog, CatalogEvent } from "../types";

export function EventCatalog({
  catalog,
  loading,
  error,
  onEvent,
}: {
  catalog: Catalog | null;
  loading: boolean;
  error: string;
  onEvent: (event: CatalogEvent) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [city, setCity] = useState("all");
  const [pastOpen, setPastOpen] = useState(false);
  const events = useMemo(() => catalog?.cities.flatMap((item) => item.events) ?? [], [catalog]);
  const upcoming = sortCatalogEvents(events.filter((event) => event.temporal_status === "upcoming"));
  const past = events.filter((event) => event.temporal_status === "past").sort((a, b) => b.event_date.localeCompare(a.event_date));
  const filtered = city === "all" ? upcoming : upcoming.filter((event) => event.city_slug === city);
  const visible = expanded ? filtered : upcoming.slice(0, 6);
  const cities = catalog?.cities.filter((item) => item.events.some((event) => event.temporal_status === "upcoming")) ?? [];

  return (
    <section className="catalog-section" id="termine" aria-labelledby="catalog-title">
      <div className="catalog-heading">
        <div><span className="section-kicker"><CalendarDays size={14} /> Aktuelle Termine</span><h2 id="catalog-title">Finde deinen Hofflohmarkt</h2><p>Tourpläne werden automatisch ergänzt, sobald sie auf hofflohmaerkte.de erscheinen.</p></div>
        {catalog?.last_crawled_at && <span className="catalog-updated">Aktualisiert {relativeTime(catalog.last_crawled_at)}</span>}
      </div>

      {loading && <div className="catalog-loading" aria-label="Termine werden geladen"><span /><span /><span /></div>}
      {!loading && error && <div className="catalog-error" role="alert"><AlertCircle size={19} /><span><strong>Termine konnten nicht geladen werden.</strong>{error}</span></div>}
      {!loading && !error && upcoming.length === 0 && <div className="catalog-empty"><CalendarClock size={25} /><strong>Noch keine kommenden Termine gefunden</strong><span>Der Katalog wird einmal täglich aktualisiert.</span></div>}

      {!loading && visible.length > 0 && (
        <>
          {expanded && (
            <div className="city-filters" aria-label="Nach Stadt filtern">
              <button className={city === "all" ? "active" : ""} onClick={() => setCity("all")}>Alle Städte <span>{upcoming.length}</span></button>
              {cities.map((item) => <button className={city === item.slug ? "active" : ""} key={item.id} onClick={() => setCity(item.slug)}>{item.name} <span>{item.events.filter((event) => event.temporal_status === "upcoming").length}</span></button>)}
            </div>
          )}
          <div className="event-grid">
            {visible.map((event) => <EventCard key={event.id} event={event} onClick={() => onEvent(event)} />)}
          </div>
          {upcoming.length > 6 && (
            <button className="catalog-toggle" onClick={() => { setExpanded((value) => !value); setCity("all"); }} aria-expanded={expanded}>
              {expanded ? "Weniger Termine" : `Alle ${upcoming.length} Termine anzeigen`} <ChevronDown size={17} className={expanded ? "turn" : ""} />
            </button>
          )}
          {expanded && past.length > 0 && (
            <div className="past-events">
              <button onClick={() => setPastOpen((value) => !value)} aria-expanded={pastOpen}><span><CalendarClock size={16} /> Vergangene Termine</span><span>{past.length}<ChevronRight size={16} className={pastOpen ? "turn" : ""} /></span></button>
              {pastOpen && <div className="event-grid past-grid">{past.map((event) => <EventCard key={event.id} event={event} onClick={() => onEvent(event)} />)}</div>}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function EventCard({ event, onClick }: { event: CatalogEvent; onClick: () => void }) {
  const status = getStatus(event);
  const actionable = isEventActionable(event);
  const content = (
    <>
      <time className="event-date" dateTime={event.event_date}><strong>{day(event.event_date)}</strong><span>{month(event.event_date)}</span><small>{weekday(event.event_date)}</small></time>
      <span className="event-copy"><small>{event.city_name}</small><strong>{event.locality}</strong><span>{timeRange(event)}</span></span>
      <span className={`event-state state-${status.tone}`}>{status.icon}<span><strong>{status.label}</strong><small>{status.detail}</small></span></span>
      {actionable && <ChevronRight className="event-arrow" size={18} />}
    </>
  );
  if (!actionable) return <article className="event-card is-muted">{content}</article>;
  return <button className="event-card" onClick={onClick} aria-label={`${event.locality}, ${event.city_name}: ${status.label}`}>{content}</button>;
}

function getStatus(event: CatalogEvent) {
  if (event.map_status === "available") return { tone: "ready", label: "Karte öffnen", detail: "bereits verarbeitet", icon: <CheckCircle2 size={17} /> };
  if (event.map_status === "stale") return { tone: "update", label: "Karte öffnen", detail: "neuer Tourplan", icon: <RefreshCw size={17} /> };
  if (event.map_status === "processing") return { tone: "live", label: "Wird erstellt", detail: `${event.run_progress ?? 0} %`, icon: <LoaderCircle className="spin" size={17} /> };
  if (event.map_status === "review") return { tone: "update", label: "Abgleich nötig", detail: "Kontrollpunkte setzen", icon: <Map size={17} /> };
  if (event.map_status === "failed") return { tone: "failed", label: "Erneut versuchen", detail: "letzter Versuch fehlgeschlagen", icon: <AlertCircle size={17} /> };
  if (event.has_pdf) return { tone: "create", label: "Karte erstellen", detail: "Tourplan verfügbar", icon: <Sparkles size={17} /> };
  return { tone: "waiting", label: "Tourplan folgt", detail: "ca. eine Woche vorher", icon: <Map size={17} /> };
}

function localDate(value: string) { return new Date(`${value}T12:00:00`); }
function day(value: string) { return new Intl.DateTimeFormat("de-DE", { day: "2-digit" }).format(localDate(value)); }
function month(value: string) { return new Intl.DateTimeFormat("de-DE", { month: "short" }).format(localDate(value)).replace(".", ""); }
function weekday(value: string) { return new Intl.DateTimeFormat("de-DE", { weekday: "short" }).format(localDate(value)).replace(".", ""); }
function timeRange(event: CatalogEvent) { return event.start_time && event.end_time ? `${event.start_time.slice(0, 5)}–${event.end_time.slice(0, 5)} Uhr` : "Uhrzeit folgt"; }
function relativeTime(value: string) {
  const date = new Date(value);
  const diff = Math.round((Date.now() - date.getTime()) / 60000);
  if (diff < 2) return "gerade eben";
  if (diff < 60) return `vor ${diff} Min.`;
  if (diff < 1440) return `vor ${Math.round(diff / 60)} Std.`;
  return new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit" }).format(date);
}
